import { useEffect, useState } from 'react';
import { Room, RoomMember } from '../../services/graphQlTypes.generated';
import { useGetRoomsForTenantQuery } from '../../services';
import { getProviderName } from '../../shared/getProviderName/getProviderName';
import { useIntl } from '@doxyme/locale';

/**
 * Room with member details and a display name.
 * Name is either the shared room display name or the owner's formatted name (via getProviderName).
 */
export type RoomWithMembers = Room & {
  members: RoomMember[];
  // The formatted display name for the room - either the shared room display name or the owner's formatted name
  name: string;
};

type UseRoomsForTenantReturn = {
  rooms: RoomWithMembers[];
  isLoading: boolean;
};

/**
 * Fetches all rooms for the current tenant.
 * The API returns a deduplicated member list plus edges (room + roomMemberIds) to avoid duplication.
 * This hook re-attaches the member details to the rooms.
 * and derives a human-readable name per room (owner via getProviderName, or shared room display name).
 */
export const useRoomsForTenant = (): UseRoomsForTenantReturn => {
  const { data, isLoading } = useGetRoomsForTenantQuery();
  const { formatMessage } = useIntl();
  const [rooms, setRooms] = useState<RoomWithMembers[]>([]);

  useEffect(() => {
    if (!data) return;

    // Distinct list of members who have access to at least one room.
    const members = data.Room.getRoomsForTenant.members ?? [];

    // Edges containing rooms with associated room member IDs.
    const edges = data.Room.getRoomsForTenant.edges ?? [];

    // Map of member IDs to member details. Makes it easier to lookup members by ID.
    const membersById = new Map(members.map((m) => [m.id, m]));

    const newRooms = edges.map((edge) => {
      const { room, roomMemberIds } = edge.node;

      const roomMembers = roomMemberIds
        .map((id) => membersById.get(id))
        .filter((m): m is RoomMember => m != null);

      const owner = room.ownerId ? membersById.get(room.ownerId) : null;

      const name = owner
        ? getProviderName({
            title: owner.title,
            firstName: owner.firstName,
            lastName: owner.lastName,
            displayName: owner.displayName,
            formatMessage,
          })
        : room.sharedRoomDisplayName ?? '';

      return { ...room, members: roomMembers, name };
    });

    setRooms(newRooms);
  }, [data]);

  return { rooms, isLoading };
};
